import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../context/AuthContext';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';

import L from 'leaflet';
import { useSpinner } from '../context/SpinnerContext';

// Fix leaflet icon issue for React environment
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Reverse geocode with Google Maps API
async function fetchLocationName(lat, lon) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return '';

  const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${apiKey}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch location name');
  const data = await response.json();

  if (data.status !== 'OK') throw new Error('Geocoding API error');
  return data.results[0]?.formatted_address || '';
}

// Get timezone from Google Time Zone API\]\
async function fetchTimeZone(lat, lon) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return '';
  const timestamp = Math.floor(Date.now() / 1000);
  const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lon}&timestamp=${timestamp}&key=${apiKey}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error('Failed to fetch timezone');
  const data = await response.json();
  return data.timeZoneId || '';
}

function formatDateForInput(dateString) {
  if (!dateString) return '';

  const dateObj = new Date(dateString);
  if (isNaN(dateObj.getTime())) return '';

  // Extract local date/time parts to avoid timezone issues
  const pad = (num) => num.toString().padStart(2, '0');

  const year = dateObj.getFullYear();
  const month = pad(dateObj.getMonth() + 1);
  const day = pad(dateObj.getDate());
  const hours = pad(dateObj.getHours());
  const minutes = pad(dateObj.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const PostForm = ({ post = {}, onSave, onCancel, isEditing, timezones = [] }) => {
  const { user } = useAuth();

  const [title, setTitle] = useState(post.title || '');
  const [content, setContent] = useState(post.content || '');
  const [location, setLocation] = useState(post.location || '');
  const [timezone, setTimezone] = useState(post.timezone || '');
  //const [coordinates, setCoordinates] = useState(post.coordinates || null);
  const [coordinates, setCoordinates] = useState([49.2827, -123.1207]); // Vancouver

  const [existingImages, setExistingImages] = useState(post.images || []);
  const [newImages, setNewImages] = useState([]);

  const mapRef = useRef(null);
  const [date, setDate] = useState(formatDateForInput(post.date));

  const hasTriedGeolocation = useRef(false);

  useEffect(() => {
    if (!timezone) {
      const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTZ) {
        setTimezone(browserTZ);
      }
    }
  }, [timezone]);

  // Get current location on mount if no coordinates already present
  useEffect(() => {
    if (!coordinates && navigator.geolocation && !hasTriedGeolocation.current) {
      hasTriedGeolocation.current = true;

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const latlng = [pos.coords.latitude, pos.coords.longitude];
          console.log("Geolocation successful:", latlng);
          setCoordinates(latlng);
        },
        (error) => {
          console.warn("Geolocation failed:", error.message);
          setCoordinates([49.2827, -123.1207]); // Default to Vancouver
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    }
  }, [coordinates]);
  // Update map view when coordinates change
  useEffect(() => {
    if (mapRef.current && coordinates) {
      const currentMap = mapRef.current;
      currentMap.setView(coordinates, 13);
    }
  }, [coordinates]);

  useEffect(() => {
    setTitle(post.title || '');
    setContent(post.content || '');
    setLocation(post.location || '');
    setTimezone(post.timezone || '');
    setCoordinates(post.coordinates ?? [49.2827, -123.1207]);
    setExistingImages(post.images || []);
    setNewImages([]);
    setDate(formatDateForInput(post.post_date));
  }, [post]);

  // Dropzone for new images
  const onDrop = useCallback((acceptedFiles) => {
    const filesWithPreview = acceptedFiles.map(file =>
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
    setNewImages((prev) => [...prev, ...filesWithPreview]);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ 
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.mov', '.avi'],
    },
  });

  const removeExistingImage = (id) => {
    setExistingImages(prev => prev.filter(img => img.id !== id));
  };

  const removeNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  // When user clicks on map, set coordinates and fetch location and timezone
  const handleMapClick = async ({ lat, lng }) => {
    console.log('Map clicked:', lat, lng);
    setCoordinates([lat, lng]);

    try {
      const locationName = await fetchLocationName(lat, lng);
      setLocation(locationName);

      const tz = await fetchTimeZone(lat, lng);
      setTimezone(tz);
    } catch (error) {
      console.error('Error fetching location or timezone:', error);
    }
  };

  // Helper to ensure location/timezone are filled before submit
  const ensureLocationAndTimezone = async () => {
    if (!coordinates) return { loc: '', tz: '' };

    let loc = location;
    let tz = timezone;

    if (!loc) {
      try {
        loc = await fetchLocationName(coordinates[0], coordinates[1]);
      } catch (e) {
        console.error('Failed to fetch location on submit:', e);
        loc = '';
      }
    }

    if (!tz) {
      try {
        tz = await fetchTimeZone(coordinates[0], coordinates[1]);
      } catch (e) {
        console.error('Failed to fetch timezone on submit:', e);
        tz = '';
      }
    }

    setLocation(loc);
    setTimezone(tz);

    return { loc, tz };
  };

  const { setLoading } = useSpinner();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { loc, tz } = await ensureLocationAndTimezone();

      const finalTimezone = tz || timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || '';

      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('location', loc || '');
      formData.append('timezone', finalTimezone);
      formData.append('post_date', date);

      if (coordinates) {
        formData.append('latitude', coordinates[0]);
        formData.append('longitude', coordinates[1]);
      }

      newImages.forEach(file => formData.append('media', file));

      formData.append('existing_image_ids', JSON.stringify(existingImages.map(img => img.id)));

      await onSave(formData, post.id);
    
    } catch (err) {
      console.error('Error saving post:', err);
    
    } finally {
      setLoading(false);
    }
  };

  if (!user?.is_superuser) {
    return <p style={{ color: 'gray', fontStyle: 'italic' }}>You do not have permission to create or edit posts.</p>;
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginBottom: '2rem' }}>
      <input
        type="text"
        placeholder="Post Title"
        value={title}
        onChange={e => setTitle(e.target.value)}
        required
        style={{ padding: 10, width: '100%', marginBottom: 10 }}
      />

      {isEditing && (
        <>
          <label htmlFor="post-date" style={{ display: 'block', marginBottom: 4, marginTop: 10 }}>
            Post Date
          </label>
          <input
            id="post-date"
            type="datetime-local"
            value={date}
            onChange={e => setDate(e.target.value)}
            required
            style={{ padding: 10, width: '100%', marginBottom: 10 }}
          />

          <label htmlFor="timezone" style={{ display: 'block', marginBottom: 4, marginTop: 10 }}>
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={e => setTimezone(e.target.value)}
            required
            style={{ padding: 10, width: '100%', marginBottom: 10 }}
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </>
      )}

      <Editor
        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
        value={content}
        init={{
          height: 300,
          menubar: false,
          plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount emoticons',
          toolbar: 'undo redo | bold italic underline | forecolor | fontsize fontfamily | bullist | emoticons | blockquote',
          fontsize_formats: '8pt 10pt 12pt 14pt 18pt 24pt 36pt',
          font_formats:
            'Arial=arial,helvetica,sans-serif;' +
            'Courier New=courier new,courier,monospace;' +
            'Georgia=georgia,palatino;' +
            'Tahoma=tahoma,arial,helvetica,sans-serif;' +
            'Times New Roman=times new roman,times;' +
            'Verdana=verdana,geneva;',
          branding: false,
          content_style: `
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 14px;
            }
          `,
        }}
        onEditorChange={setContent}
      />

      {/* LOCATION and TIMEZONE inputs REMOVED */}

      <div style={{ height: 300, marginBottom: 10 }}>
        {coordinates ? (
          <MapContainer
            center={coordinates}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            whenCreated={mapInstance => { mapRef.current = mapInstance; }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            <MapClickHandler onClick={handleMapClick} />
            {coordinates && <Marker position={coordinates} />}
          </MapContainer>
        ) : (
          <p>Loading map...</p>
        )}
      </div>

      <div {...getRootProps()} style={{ border: '2px dashed #ccc', padding: 10, marginBottom: 10 }}>
        <input {...getInputProps()} />
        <p>Drag & drop images here, or click to select</p>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      {existingImages.map(img => (
        <div key={img.id} style={{ position: 'relative' }}>
          {img.type && img.type.startsWith('video/') ? (
            <video src={img.image} width={100} controls />
          ) : (
            <img src={img.image} alt="uploaded" width={100} />
          )}
          <button
            type="button"
            onClick={() => removeExistingImage(img.id)}
            style={removeButtonStyle}
            aria-label="Remove existing media"
          >
            ×
          </button>
        </div>
      ))}
      {newImages.map((file, index) => (
        <div key={`${file.name}-${index}`} style={{ position: 'relative' }}>
          {file.type.startsWith('video/') ? (
            <video
              src={file.preview}
              width={100}
              controls
              style={{ display: 'block' }}
            />
          ) : (
            <img src={file.preview} alt="preview" width={100} />
          )}
          <button
            type="button"
            onClick={() => removeNewImage(index)}
            style={removeButtonStyle}
            aria-label="Remove new media"
          >
            ×
          </button>
        </div>
      ))}
      </div>

      <div style={{ marginTop: 10 }}>
        <button type="submit" style={submitButtonStyle}>{isEditing ? 'Update Post' : 'Create Post'}</button>
        <button type="button" onClick={onCancel} style={cancelButtonStyle}>Cancel</button>
      </div>
    </form>
  );
};

// Small helper component to catch map clicks and call onClick with [lat, lon]
function MapClickHandler({ onClick }) {
  useMapEvents({
    click(e) {
      const lat = e.latlng.lat;
      const lon = normalizeLongitude(e.latlng.lng);
      console.log('Map clicked and normalized:', lat, lon);
      onClick({ lat, lng: lon });
    },
  });
  return null;
}

function normalizeLongitude(lon) {
  while (lon < -180) lon += 360;
  while (lon > 180) lon -= 360;
  return lon;
}

const removeButtonStyle = {
  position: 'absolute',
  top: 2,
  right: 2,
  background: 'red',
  color: 'white',
  border: 'none',
  borderRadius: '50%',
  width: 20,
  height: 20,
  cursor: 'pointer',
  fontWeight: 'bold',
};

const submitButtonStyle = {
  backgroundColor: '#007BFF',
  color: 'white',
  padding: '10px 15px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  marginRight: 10,
};

const cancelButtonStyle = {
  backgroundColor: '#6c757d',
  color: 'white',
  padding: '10px 15px',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

export default PostForm;